<template>
  <div class="db-tw-mb-9 db-tw-grid db-tw-grid-cols-2 db-tw-gap-3">
    <div>
      <DbSelectCustom
        :modelValue="startTime"
        :options="optionsWithEmptyValue"
        :class="[{ 'db-has-error': v$.startTime.$error }]"
        :inputAttrs="{ required: isMandatoryStartTime }"
        v-on:update:model-value="$emit('update:startTime', $event)"
      >
        <span class="db-label">{{ labelStartTime }}</span>
        <template
          v-if="v$.startTime.$error"
          v-slot:error
        >
          <DbRfpErrors :errors="v$.startTime.$errors" />
        </template>
      </DbSelectCustom>
    </div>
    <div>
      <DbSelectCustom
        :modelValue="endTime"
        :options="availableEndTimes"
        :class="[{ 'db-has-error': v$.endTime.$error }]"
        :inputAttrs="{ required: isMandatoryEndTime }"
        v-on:update:model-value="$emit('update:endTime', $event)"
      >
        <span class="db-label">{{ labelEndTime }}</span>
        <template
          v-if="v$.endTime.$error"
          v-slot:error
        >
          <DbRfpErrors :errors="v$.endTime.$errors" />
        </template>
      </DbSelectCustom>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { useI18n } from 'vue-i18n'
import { requiredIf } from '@/utils/i18n-validators'

const props = withDefaults(
  defineProps<{
    /** Label of the field */
    labelStartTime: string
    labelEndTime: string
    /** Selected value */
    startTime?: string
    endTime?: string
    isMandatoryStartTime: boolean
    isMandatoryEndTime: boolean
  }>(),
  {
    startTime: '',
    endTime: '',
  },
)

const emit = defineEmits<{
  (e: 'update:startTime', startTime: string): void
  (e: 'update:endTime', endTime: string): void
}>()

const { t } = useI18n()
const optionsWithEmptyValue = computed(() => {
  const defaultValue = {
    label: t('Select item'),
    value: '',
  }
  // 8 to 12am in increments of 30mins
  const times = [
    t('8:00 AM'),
    t('8:30 AM'),
    t('9:00 AM'),
    t('9:30 AM'),
    t('10:00 AM'),
    t('10:30 AM'),
    t('11:00 AM'),
    t('11:30 AM'),
    t('12:00 PM'),
    t('12:30 PM'),
    t('1:00 PM'),
    t('1:30 PM'),
    t('2:00 PM'),
    t('2:30 PM'),
    t('3:00 PM'),
    t('3:30 PM'),
    t('4:00 PM'),
    t('4:30 PM'),
    t('5:00 PM'),
    t('5:30 PM'),
    t('6:00 PM'),
    t('6:30 PM'),
    t('7:00 PM'),
    t('7:30 PM'),
    t('8:00 PM'),
    t('8:30 PM'),
    t('9:00 PM'),
    t('9:30 PM'),
    t('10:00 PM'),
    t('10:30 PM'),
    t('11:00 PM'),
    t('11:30 PM'),
    t('12:00 AM'),
  ]
  return [defaultValue, ...times.map((time) => ({ label: time, value: time }))]
})

const startTimeIndex = computed(() => {
  return optionsWithEmptyValue.value.findIndex((option) => option.value === props.startTime)
})

const endTimeIndex = computed(() => {
  return optionsWithEmptyValue.value.findIndex((option) => option.value === props.endTime)
})

/** List available times for "End time", that are AFTER the choosen start value */
const availableEndTimes = computed(() => {
  if (!props.startTime) return optionsWithEmptyValue.value

  const newValIndex = startTimeIndex.value

  return optionsWithEmptyValue.value.map((option, index) => {
    return {
      ...option,
      isDisabled: index === 0 ? false : index <= newValIndex,
    }
  })
})

/** If start time if after an existing end time, we clear end time */
function clearEndTimeIfBeforeStartTime() {
  const newValIndex = startTimeIndex.value
  const endTimeValIndex = endTimeIndex.value

  const isEndTimeBeforeStartTime = endTimeValIndex ? newValIndex >= endTimeValIndex : false

  if (isEndTimeBeforeStartTime) {
    emit('update:endTime', '')
  }
}

watch(() => props.startTime, clearEndTimeIfBeforeStartTime)

const state = computed(() => {
  return {
    startTime: props.startTime,
    endTime: props.endTime,
  }
})

const rules = {
  startTime: { required: requiredIf(props.isMandatoryStartTime) },
  endTime: { required: requiredIf(props.isMandatoryEndTime) },
}

const v$ = useVuelidate(rules, state, { $autoDirty: true })
</script>
